import type { NewParlay } from '@hooks/useTrackedParlays';
import type { OptimizedParlaySet } from '@parlay-picker/shared/api/mlApi';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import { deriveParlayLeague } from '@parlay-picker/shared/league';
import type { OptimizerLeg } from './OptimizerModal';

type OptimizedParlay = OptimizedParlaySet['parlays'][number];

/** The track request for one optimized parlay; each leg keeps its input leg's identity and league. */
export function optimizedParlayToNewParlay(
  parlay: OptimizedParlay,
  inputLegs: OptimizerLeg[],
): NewParlay {
  const legBooks = new Set<string | undefined>();
  const legs = parlay.legs.map((leg) => {
    const inputLeg = inputLegs.find((input) => input.id === leg.id);
    // An optimized leg carries no league of its own, so one that is not an input cannot be tracked.
    if (!inputLeg) throw new Error(`Optimized leg ${leg.id} is not one of the input legs`);
    legBooks.add(inputLeg.sportsbook);
    return {
      player: leg.player ?? '',
      team: leg.team ?? '',
      opponentTeam: inputLeg.opponent ?? null,
      isHomeGame: inputLeg.isHomeGame ?? null,
      prop: `${leg.overUnder ?? ''} ${leg.line ?? ''} ${leg.statType}`.trim(),
      statType: leg.statType,
      line: leg.line,
      overUnder: leg.overUnder,
      odds: leg.odds,
      gameId: inputLeg.gameId,
      leagueKey: inputLeg.leagueKey,
      provenance: inputLeg.provenance,
      predictionId: inputLeg.predictionId,
      factorsUsed: inputLeg.factorsUsed,
    };
  });
  const [sportsbook, ...otherBooks] = legBooks;
  if (!sportsbook || otherBooks.length > 0) {
    throw new Error(`Optimized parlay ${parlay.tier} does not come from one sportsbook`);
  }
  return {
    legs,
    sportsbook,
    name: parlay.tier,
    totalOdds: parlay.combinedOdds ?? '',
    origins: ['ai'],
    league: deriveParlayLeague(legs.map((leg) => leg.leagueKey)),
  };
}

/**
 * One optimizer leg per distinct pick across the generated parlays. Parlays are walked in
 * index order, so a pick held by several keeps the prediction id of the lowest index.
 */
export function uniqueOptimizerLegsFrom(recs: GeneratedParlay[]): OptimizerLeg[] {
  const seen = new Set<string>();
  const legs: OptimizerLeg[] = [];
  let idx = 0;
  for (const parlay of recs) {
    for (const leg of parlay.legs ?? []) {
      const key = `${(leg.player ?? '').toLowerCase()}:${leg.statType}:${leg.line}:${leg.overUnder}`;
      if (seen.has(key)) continue;
      seen.add(key);
      legs.push({
        id: `gen-${idx++}`,
        player: leg.player ?? '',
        team: leg.team ?? '',
        statType: leg.statType ?? '',
        line: leg.line ?? null,
        overUnder: leg.overUnder ?? null,
        odds: leg.odds ?? '-110',
        gameId: leg.gameId ?? '',
        score: 0,
        sportsbook: parlay.sportsbook,
        leagueKey: leg.leagueKey,
        opponent: leg.opponentTeam ?? null,
        isHomeGame: leg.isHomeGame ?? null,
        aiEdge: leg.analysis?.edge ?? null,
        aiReasoning: leg.explanation?.summary ?? null,
        provenance: 'generation',
        predictionId: leg.predictionId,
      });
    }
  }
  return legs;
}
